import {
  DIATONIC_TRIADS,
  SCALE_OCTAVE,
  SCORE_VALUES,
  TETRACORDS,
  sequenceMatchesPattern,
} from './scale.js';

const RULE_CHECKS = [
  {
    name: 'Scale Match',
    score: SCORE_VALUES.scale,
    len: SCALE_OCTAVE.length,
    test: (slice) => sequenceMatchesPattern(slice, SCALE_OCTAVE),
  },
  {
    name: 'Tetracord Match',
    score: SCORE_VALUES.tetracord,
    len: 4,
    test: (slice) => TETRACORDS.some((p) => sequenceMatchesPattern(slice, p)),
  },
  {
    name: 'Triad Match',
    score: SCORE_VALUES.triad,
    len: 3,
    test: (slice) => DIATONIC_TRIADS.some((p) => sequenceMatchesPattern(slice, p)),
  },
  {
    name: 'Octave Match',
    score: SCORE_VALUES.octave,
    len: 3,
    test: (slice) => slice.every((n) => n === slice[0]),
  },
];

/**
 * @param {import('./board.js').Board} board
 * @returns {{ keys: Set<string>, matches: { rule: string, notes: string[], score: number }[] }}
 */
export function findMusicalMatches(board) {
  const keys = new Set();
  const matches = [];
  const runs = collectLineRuns(board);

  for (const run of runs) {
    const hit = findBestMatchInRun(run.cells);
    if (!hit) continue;

    const slice = run.cells.slice(hit.start, hit.start + hit.len);
    for (const cell of slice) {
      keys.add(`${cell.x},${cell.y}`);
    }
    const noteStr = slice.map((c) => c.note).join('-');
    matches.push({
      rule: hit.name,
      notes: slice.map((c) => c.note),
      score: hit.score,
      cells: slice.map((c) => ({ x: c.x, y: c.y })),
    });
    console.log(`[Musical Cloud Tetris] ${hit.name}: ${noteStr} (+${hit.score})`);
  }

  return { keys, matches };
}

/** @param {{ x: number, y: number, note: string }[]} cells */
function findBestMatchInRun(cells) {
  const notes = cells.map((c) => c.note);
  let best = null;

  for (const rule of RULE_CHECKS) {
    if (notes.length < rule.len) continue;
    for (let start = 0; start <= notes.length - rule.len; start++) {
      const slice = notes.slice(start, start + rule.len);
      if (rule.test(slice)) {
        return { name: rule.name, score: rule.score, start, len: rule.len };
      }
    }
  }

  return best;
}

/** @param {import('./board.js').Board} board */
function collectLineRuns(board) {
  const runs = [];

  for (let y = 0; y < board.rows; y++) {
    let x = 0;
    while (x < board.cols) {
      if (!board.grid[y][x]) {
        x++;
        continue;
      }
      const cells = [];
      let cx = x;
      while (cx < board.cols && board.grid[y][cx]) {
        cells.push({ x: cx, y, note: board.grid[y][cx].note, color: board.grid[y][cx].color });
        cx++;
      }
      if (cells.length >= 3) runs.push({ cells });
      x = cx;
    }
  }

  for (let x = 0; x < board.cols; x++) {
    let y = 0;
    while (y < board.rows) {
      if (!board.grid[y][x]) {
        y++;
        continue;
      }
      const cells = [];
      let cy = y;
      while (cy < board.rows && board.grid[cy][x]) {
        cells.push({ x, y: cy, note: board.grid[cy][x].note, color: board.grid[cy][x].color });
        cy++;
      }
      if (cells.length >= 3) runs.push({ cells });
      y = cy;
    }
  }

  return runs;
}

/**
 * @param {import('./board.js').Board} board
 * @returns {{ totalScore: number, matchLog: { rule: string, notes: string[], score: number }[] }}
 */
export function resolveMusicalClears(board) {
  let totalScore = 0;
  const matchLog = [];

  while (true) {
    const { keys, matches } = findMusicalMatches(board);
    if (keys.size === 0) break;
    board.clearCells(keys);
    for (const m of matches) {
      totalScore += m.score;
      matchLog.push(m);
    }
  }

  return { totalScore, matchLog };
}
